/**
 * QuickBooks Async Upload Hook
 *
 * Custom hook for managing async QuickBooks sales receipt uploads.
 * Handles upload initiation, progress polling, and state management.
 *
 * Features:
 * - Automatic progress polling every 2 seconds
 * - Cleanup on unmount to prevent memory leaks
 * - Timeout handling (max 10 minutes)
 * - Error handling with user-friendly messages
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  useUploadReceiptsMutation,
  useLazyGetUploadProgressQuery,
} from '../api/quickbooksApi';
import type { UploadProgress, GroupedTransactions } from '../types/quickbooks';

interface UploadData {
  studioId: string;
  payment_types: string[];
  grouped_transactions: GroupedTransactions;
  file_name: string;
}

interface UseQuickBooksUploadReturn {
  uploadReceipts: (data: UploadData) => Promise<void>;
  isUploading: boolean;
  progress: UploadProgress | null;
  taskId: string | null;
  estimatedTimeRemaining: number | null;
  showTimeoutWarning: boolean;
  reset: () => void;
}

const POLL_INTERVAL = 2000; // 2 seconds
const MAX_UPLOAD_TIME = 60 * 60 * 1000; // 60 minutes (increased for large uploads)
const TIMEOUT_WARNING_THRESHOLD = 0.8; // Warn at 80% of max time (48 minutes)

// LocalStorage keys for background upload persistence
const STORAGE_KEY_TASK_ID = 'qb_upload_task_id';
const STORAGE_KEY_STUDIO_ID = 'qb_upload_studio_id';
const STORAGE_KEY_START_TIME = 'qb_upload_start_time';

export const useQuickBooksUpload = (): UseQuickBooksUploadReturn => {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState<
    number | null
  >(null);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const lastProgressRef = useRef<{
    processed: number;
    timestamp: number;
  } | null>(null);

  const [initiateUpload] = useUploadReceiptsMutation();
  const [getProgress] = useLazyGetUploadProgressQuery();

  /**
   * Clean up polling interval and reset state
   */
  const cleanup = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    startTimeRef.current = null;
  }, []);

  /**
   * Reset all state to initial values
   */
  const reset = useCallback(() => {
    cleanup();
    setIsUploading(false);
    setProgress(null);
    setTaskId(null);
  }, [cleanup]);

  /**
   * Calculate estimated time remaining based on upload rate
   */
  const calculateTimeRemaining = useCallback(
    (processed: number, total: number): number | null => {
      if (!processed || !total || processed >= total) return null;

      const now = Date.now();

      // Need at least 2 progress updates to calculate rate
      if (!lastProgressRef.current) {
        lastProgressRef.current = { processed, timestamp: now };
        return null;
      }

      const timeDiff = (now - lastProgressRef.current.timestamp) / 1000; // seconds
      const processedDiff = processed - lastProgressRef.current.processed;

      // Update tracking
      lastProgressRef.current = { processed, timestamp: now };

      // Need significant progress to calculate meaningful rate
      if (processedDiff <= 0 || timeDiff < 2) return null;

      const receiptsPerSecond = processedDiff / timeDiff;
      const remainingReceipts = total - processed;
      const estimatedSeconds = remainingReceipts / receiptsPerSecond;

      return estimatedSeconds;
    },
    []
  );

  /**
   * Save upload state to localStorage for background persistence
   */
  const saveUploadState = useCallback(
    (uploadTaskId: string, studioId: string) => {
      try {
        localStorage.setItem(STORAGE_KEY_TASK_ID, uploadTaskId);
        localStorage.setItem(STORAGE_KEY_STUDIO_ID, studioId);
        localStorage.setItem(STORAGE_KEY_START_TIME, Date.now().toString());
      } catch (error) {
        console.warn('Failed to save upload state to localStorage:', error);
      }
    },
    []
  );

  /**
   * Clear upload state from localStorage
   */
  const clearUploadState = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY_TASK_ID);
      localStorage.removeItem(STORAGE_KEY_STUDIO_ID);
      localStorage.removeItem(STORAGE_KEY_START_TIME);
    } catch (error) {
      console.warn('Failed to clear upload state from localStorage:', error);
    }
  }, []);

  /**
   * Poll for upload progress with enhanced features
   */
  const pollProgress = useCallback(
    async (studioId: string, uploadTaskId: string) => {
      try {
        const result = await getProgress({
          studioId,
          taskId: uploadTaskId,
        }).unwrap();

        setProgress(result);

        // Calculate estimated time remaining
        if (result.processed && result.total) {
          const timeRemaining = calculateTimeRemaining(
            result.processed,
            result.total
          );
          // Only update if we got a valid estimate, otherwise keep previous value
          if (timeRemaining !== null) {
            setEstimatedTimeRemaining(timeRemaining);
          }
          // Keep displaying last valid estimate until upload completes
        }

        // Check if upload is complete or failed
        if (result.status === 'completed' || result.status === 'failed') {
          cleanup();
          clearUploadState();
          setIsUploading(false);
          setEstimatedTimeRemaining(null);
          setShowTimeoutWarning(false);
          return true; // Signal completion
        }

        // Check for timeout warning threshold (80% of max time)
        if (startTimeRef.current) {
          const elapsed = Date.now() - startTimeRef.current;
          const timeoutThreshold = MAX_UPLOAD_TIME * TIMEOUT_WARNING_THRESHOLD;

          if (elapsed > timeoutThreshold && !showTimeoutWarning) {
            setShowTimeoutWarning(true);
          }

          // Check for actual timeout
          if (elapsed > MAX_UPLOAD_TIME) {
            cleanup();
            clearUploadState();
            setProgress({
              status: 'failed',
              progress: 0,
              message:
                'Upload timed out after 60 minutes. The upload may still be processing in the background. Check upload history for results.',
              error: 'Upload timeout',
            });
            setIsUploading(false);
            setEstimatedTimeRemaining(null);
            setShowTimeoutWarning(false);
            return true; // Signal timeout
          }
        }

        return false; // Continue polling
      } catch (error: any) {
        // Network error or API error
        cleanup();
        clearUploadState();
        setProgress({
          status: 'failed',
          progress: 0,
          message:
            error?.data?.error ||
            error?.message ||
            'Failed to get upload progress. Please check your connection.',
          error: error?.message || 'Unknown error',
        });
        setIsUploading(false);
        setEstimatedTimeRemaining(null);
        setShowTimeoutWarning(false);
        return true; // Signal error
      }
    },
    [
      getProgress,
      cleanup,
      calculateTimeRemaining,
      clearUploadState,
      showTimeoutWarning,
    ]
  );

  /**
   * Start async upload
   */
  const uploadReceipts = useCallback(
    async (data: UploadData) => {
      setIsUploading(true);
      setProgress({
        status: 'pending',
        progress: 0,
        message: 'Starting upload...',
      });
      startTimeRef.current = Date.now();

      try {
        // Initiate async upload
        const response = await initiateUpload({
          studioId: data.studioId,
          payment_types: data.payment_types,
          grouped_transactions: data.grouped_transactions,
          file_name: data.file_name,
        }).unwrap();

        const uploadTaskId = response.task_id;
        setTaskId(uploadTaskId);

        // Save upload state to localStorage for background persistence
        saveUploadState(uploadTaskId, data.studioId);

        // Update progress to show upload started
        setProgress({
          status: 'started',
          progress: 0,
          message: response.message || 'Upload started...',
        });

        // Start polling for progress
        pollIntervalRef.current = setInterval(async () => {
          const isComplete = await pollProgress(data.studioId, uploadTaskId);
          if (isComplete && pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }, POLL_INTERVAL);

        // Do initial poll immediately
        await pollProgress(data.studioId, uploadTaskId);
      } catch (error: any) {
        // Error starting upload
        cleanup();
        setProgress({
          status: 'failed',
          progress: 0,
          message:
            error?.data?.error ||
            error?.message ||
            'Failed to start upload. Please try again.',
          error: error?.message || 'Unknown error',
        });
        setIsUploading(false);
      }
    },
    [initiateUpload, pollProgress, cleanup, saveUploadState]
  );

  /**
   * Check for active upload on mount and resume if found
   */
  useEffect(() => {
    const resumeActiveUpload = async () => {
      try {
        const savedTaskId = localStorage.getItem(STORAGE_KEY_TASK_ID);
        const savedStudioId = localStorage.getItem(STORAGE_KEY_STUDIO_ID);
        const savedStartTime = localStorage.getItem(STORAGE_KEY_START_TIME);

        if (savedTaskId && savedStudioId && savedStartTime) {
          console.log('Found active upload, resuming...', savedTaskId);

          // Set state to uploading
          setIsUploading(true);
          setTaskId(savedTaskId);
          startTimeRef.current = parseInt(savedStartTime, 10);

          // Start polling for progress
          pollIntervalRef.current = setInterval(async () => {
            const isComplete = await pollProgress(savedStudioId, savedTaskId);
            if (isComplete && pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
          }, POLL_INTERVAL);

          // Do initial poll immediately
          await pollProgress(savedStudioId, savedTaskId);
        }
      } catch (error) {
        console.error('Failed to resume active upload:', error);
        clearUploadState();
      }
    };

    resumeActiveUpload();
  }, [pollProgress, clearUploadState]);

  /**
   * Cleanup on unmount
   */
  useEffect(
    () => () => {
      cleanup();
    },
    [cleanup]
  );

  return {
    uploadReceipts,
    isUploading,
    progress,
    taskId,
    estimatedTimeRemaining,
    showTimeoutWarning,
    reset,
  };
};
